import React, { Component } from 'react';
import { object } from 'prop-types';
import { Card, Tag, Row, Col, Descriptions, Button, Collapse, Empty } from 'antd';
import {
  BankOutlined,
  EnvironmentOutlined,
  ArrowLeftOutlined,
  LinkOutlined,
  WarningOutlined
} from '@ant-design/icons';
import { Link } from 'react-router-dom';
import { groupBy } from 'lodash';
import moment from 'moment';

import { history } from '../../common/custom-router';
import DaysUntilBadge from '../days-until-badge';
import StageTimeline from '../stage-timeline';

import './index.scss';

const { Panel } = Collapse;

class ClientDetail extends Component {
  static propTypes = {
    client: object
  };

  getStageStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'default';
      case 'ongoing': return 'processing';
      case 'finished': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  copyBuyerLink = (publicId) => {
    const url = `${window.location.origin}/buyers/clients/${publicId}`;
    navigator.clipboard.writeText(url);
  };

  renderSaleProcessCard = (sp) => {
    const currentStage = sp.currentStage;

    return (
      <Card
        key={sp.id}
        className="sale-process-card"
        hoverable
        onClick={() => history.push(`/staff/sale_processes/${sp.id}`)}
      >
        <Row gutter={16}>
          <Col flex="auto">
            <h4 className="sp-address">
              <EnvironmentOutlined className="sp-icon" />
              {sp.name || 'Sin dirección'}
            </h4>
            <p className="sp-type">{sp.processType && sp.processType.name}</p>
          </Col>
          <Col>
            {currentStage && (
              <Tag color={this.getStageStatusColor(currentStage.status)}>
                {currentStage.name}
              </Tag>
            )}
            {!sp.ended && sp.daysUntilStageEnd !== undefined && (
              <DaysUntilBadge daysUntil={sp.daysUntilStageEnd} />
            )}
            {!sp.ended && sp.daysUntilStageEnd !== undefined && sp.daysUntilStageEnd <= 0 && (
              <Tag color="warning" icon={<WarningOutlined />}>Atrasado</Tag>
            )}
            {sp.ended && <Tag color="green">Finalizado</Tag>}
          </Col>
        </Row>
        <Row gutter={16} style={{ marginTop: 8 }}>
          <Col>
            <span className="sp-detail">
              <strong>Supervisor:</strong>{' '}
              {sp.supervisor && `${sp.supervisor.firstName} ${sp.supervisor.lastName}`}
            </span>
          </Col>
          <Col>
            <span className="sp-detail">
              <strong>Creado:</strong> {moment(sp.createdAt).format('DD/MM/YYYY')}
            </span>
          </Col>
          {sp.tags && sp.tags.length > 0 && (
            <Col>
              {sp.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
            </Col>
          )}
        </Row>
        {sp.stages && sp.stages.length > 0 && (
          <StageTimeline
            stages={sp.stages}
            currentStageId={currentStage && currentStage.id}
          />
        )}
        <Row style={{ marginTop: 8 }}>
          <Col>
            <Link
              to={`/buyers/sale_processes/${sp.buyerPublicId}`}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
            >
              <Button size="small" type="link">
                <LinkOutlined /> Ver como comprador
              </Button>
            </Link>
          </Col>
        </Row>
      </Card>
    );
  };

  renderGroupedByBank = (saleProcesses) => {
    const bankNamesFor = (sp) => (sp.creditInfos || [])
      .map((ci) => ci && ci.bankName)
      .filter(Boolean);

    const withBank = saleProcesses.filter(
      (sp) => bankNamesFor(sp).length > 0
    );
    const withoutBank = saleProcesses.filter(
      (sp) => bankNamesFor(sp).length === 0
    );

    const pairs = withBank.flatMap(
      (sp) => [...new Set(bankNamesFor(sp))].map((bankName) => [bankName, sp])
    );
    const grouped = groupBy(pairs, ([bankName]) => bankName);
    Object.keys(grouped).forEach((bankName) => {
      grouped[bankName] = grouped[bankName].map(([, sp]) => sp);
    });

    return (
      <>
        {Object.entries(grouped).map(([bankName, processes]) => (
          <Collapse
            key={bankName}
            defaultActiveKey={[bankName]}
            className="bank-collapse"
          >
            <Panel
              header={
                <span className="bank-header">
                  <BankOutlined style={{ marginRight: 8 }} />
                  {bankName}
                  <Tag style={{ marginLeft: 8 }} color="blue">
                    {processes.length}
                  </Tag>
                </span>
              }
              key={bankName}
            >
              {processes.map(this.renderSaleProcessCard)}
            </Panel>
          </Collapse>
        ))}
        {withoutBank.length > 0 && (
          <Collapse
            defaultActiveKey={['sin-banco']}
            className="bank-collapse"
          >
            <Panel
              header={
                <span className="bank-header">
                  <BankOutlined style={{ marginRight: 8 }} />
                  Sin banco asignado
                  <Tag style={{ marginLeft: 8 }}>{withoutBank.length}</Tag>
                </span>
              }
              key="sin-banco"
            >
              {withoutBank.map(this.renderSaleProcessCard)}
            </Panel>
          </Collapse>
        )}
      </>
    );
  };

  render () {
    const { client } = this.props;
    if (!client) return null;

    const saleProcesses = client.saleProcesses || [];
    const activeSPs = saleProcesses.filter((sp) => !sp.ended);
    const endedSPs = saleProcesses.filter((sp) => sp.ended);

    return (
      <div style={{ padding: '30px' }}>
        <Row align="middle" style={{ marginBottom: 20 }}>
          <Col>
            <Button
              type="link"
              icon={<ArrowLeftOutlined />}
              onClick={() => history.push('/staff/clients')}
            >
              Volver a clientes
            </Button>
          </Col>
        </Row>

        <Card className="client-header-card">
          <Row gutter={24} align="middle">
            <Col flex="auto">
              <h2 style={{ margin: 0 }}>
                {client.firstName} {client.lastName}
              </h2>
              <span style={{ color: '#8c8c8c' }}>{client.rut}</span>
            </Col>
            <Col>
              <Button
                type="primary"
                ghost
                icon={<LinkOutlined />}
                onClick={() => this.copyBuyerLink(client.publicId)}
              >
                Copiar link comprador
              </Button>
            </Col>
          </Row>
          <Descriptions
            column={{ xs: 1, sm: 2, md: 3 }}
            style={{ marginTop: 16 }}
          >
            {client.email && (
              <Descriptions.Item label="Email">
                {client.email}
              </Descriptions.Item>
            )}
            {client.phoneNumber && (
              <Descriptions.Item label="Teléfono">
                {client.phoneNumber}
              </Descriptions.Item>
            )}
            {client.commune && (
              <Descriptions.Item label="Comuna">
                {client.commune}
              </Descriptions.Item>
            )}
            {client.region && (
              <Descriptions.Item label="Región">
                {client.region}
              </Descriptions.Item>
            )}
          </Descriptions>
          <Row gutter={16} style={{ marginTop: 12 }}>
            <Col>
              <Tag color="blue">
                {client.activeCount || 0} operaciones activas
              </Tag>
            </Col>
            <Col>
              <Tag>{client.endedCount || 0} finalizadas</Tag>
            </Col>
            {saleProcesses.length > 3 && (
              <Col>
                <Tag color="purple">{saleProcesses.length} propiedades</Tag>
              </Col>
            )}
          </Row>
          {client.allTags && client.allTags.length > 0 && (
            <Row style={{ marginTop: 8 }}>
              <Col>
                {client.allTags.map((tag) => (
                  <Tag key={tag} color="cyan" style={{ marginBottom: 4 }}>
                    {tag}
                  </Tag>
                ))}
              </Col>
            </Row>
          )}
        </Card>

        <h3 style={{ marginTop: 24, marginBottom: 16 }}>
          Operaciones Activas ({activeSPs.length})
        </h3>
        {activeSPs.length > 0 ? (
          this.renderGroupedByBank(activeSPs)
        ) : (
          <Empty description="Sin operaciones activas" />
        )}

        {endedSPs.length > 0 && (
          <>
            <h3 style={{ marginTop: 24, marginBottom: 16 }}>
              Operaciones Finalizadas ({endedSPs.length})
            </h3>
            {this.renderGroupedByBank(endedSPs)}
          </>
        )}
      </div>
    );
  }
}

export default ClientDetail;
